import { Translations, UUID } from '@/types/types';
import { Asset } from './AssetApi';
import { QueryKey } from '@/configs/queryKeys';
import { ProductType } from './ProductTypeApi';
import AttributeValueApi, { AttributeValue } from './AttributeValueApi';
import { Accessory } from './AccessoryApi';
import EntityApi, { BaseEntity } from './EntityApi';

export enum AccessoryMountingSide {
	FRONT = 'FRONT',
	BACK = 'BACK',
	BOTH = 'BOTH',
}

export interface AccessorySettings {
	safeDistanceTop: number;
	safeDistanceBottom: number;
	safeDistanceDepth: number;
	mountingSide: AccessoryMountingSide;
}

export interface ProductTypeVariationAccessory {
	id: UUID;
	accessoryId: UUID;
	accessory: Accessory;
	settings: AccessorySettings;
}

export interface ProductTypeVariationLengthSettings {
	handle: {
		min: number;
		max: number;
		step: number;
		onlyPredefined: boolean;
		predefinedValues: number[];
	};
	endings: {
		min: number;
		max: number;
		step: number;
	};
}

export interface ProductTypeVariation extends BaseEntity {
	name: string;
	productType: ProductType;
	model3d: Asset;
	listOrder: number;
	attributeValues: AttributeValue[];
	attributeValuesIds: UUID[];
	requiredAttributesIds: UUID[];
	variationAccessories: ProductTypeVariationAccessory[];
	defaultAttributeValues: Record<UUID, UUID | null>;
	totalHeightFormula: string;
	maxMiddlePostQuantity: number;
	middlePostMaxSpacing: number;
	autoMiddlePostThreshold: number[];
	withMiddlePost: boolean;
	accessoriesNeedsMiddlePost: boolean;
	sidePostsAdjustable: boolean;
	endingsAdjustable: boolean;
	lengthSettings: ProductTypeVariationLengthSettings;
	spacingConstant: number;
	upperPostLength: number;
	lowerPostLength: number;
	translations: Translations<{
		name: string;
	}>;
}

class ProductTypeVariationApi extends EntityApi<ProductTypeVariation> {
	constructor() {
		super(QueryKey.productTypeVariations);
		this.loadWithRelations = this.loadWithRelations.bind(this);
	}

	async loadWithRelations({
		id,
		allAccessories,
		allAttributeValues,
	}: {
		id: UUID;
		allAccessories: Accessory[];
		allAttributeValues?: AttributeValue[];
	}) {
		const variation = await super.mutationGetOne(id);

		let attributeValues = allAttributeValues;
		if (!allAttributeValues)
			attributeValues = await AttributeValueApi.mutationGetAll();

		for (const variationAccessory of variation.variationAccessories) {
			const accessory = allAccessories.find(
				(accessory) => accessory.id === variationAccessory.accessoryId
			)!;
			variationAccessory.accessory = accessory;
		}

		variation.attributeValues = variation.attributeValuesIds.map(
			(attributeValueId) =>
				attributeValues!.find(
					(attributeValue) => attributeValue.id === attributeValueId
				)!
		);

		return variation;
	}
}

export default new ProductTypeVariationApi();
